import { OPTIMIZELY_EXPERIMENTS, OPTIMIZELY_VARIATIONS } from '~/constants/optimizely.constants'
import { OPTIMIZELY_MAX_WAIT_MS, isOptimizelySdkReady } from '~/utils/optimizely/sdk-ready'
import { getLocal, setLocal, removeLocal } from '~/utils/helpers'

export const HEALTH_QUOTE_ENTRY_AB_WINDOW_KEY = 'ABTestHealthQuickQuoteVariant'
export const HEALTH_QUOTE_ENTRY_AB_STORAGE_KEY = 'abtest.healthQuickQuoteVariant'

// Module-level so every consumer shares one resolution per client session.
// Safe under SSR: resolveVariation is client-only.
const variation = ref(null)
const resolved = ref(false)
let inFlight = null
let exposureFired = false

/**
 * `verifyActive` is for the page the experiment targets (step0). There, once Optimizely has
 * loaded, a missing window value means the visitor is not in the experiment (e.g. it was
 * paused or ended), so a cached assignment is cleared rather than served. Elsewhere the
 * experiment does not run, so a missing value is expected and the cached assignment applies.
 */
const readVariation = ({ verifyActive = false } = {}) => {
  const windowValue = window[HEALTH_QUOTE_ENTRY_AB_WINDOW_KEY]

  // Explicit null = not assigned; do not revive a stale cached assignment.
  if (windowValue === null) {
    removeLocal(HEALTH_QUOTE_ENTRY_AB_STORAGE_KEY)
    return null
  }

  if (windowValue !== undefined) {
    const value = +windowValue
    setLocal(HEALTH_QUOTE_ENTRY_AB_STORAGE_KEY, value)
    return value
  }

  if (verifyActive && isOptimizelySdkReady()) {
    removeLocal(HEALTH_QUOTE_ENTRY_AB_STORAGE_KEY)
    return null
  }

  // Snippet unavailable or URL not targeted: fall back to the cached assignment.
  const stored = getLocal(HEALTH_QUOTE_ENTRY_AB_STORAGE_KEY)
  if (stored !== null && stored !== undefined) {
    return +((stored === 'true' || stored === '1') ? 1 : 0)
  }

  return null
}

// Waits (bounded) for Optimizely to expose the window value, then records the result.
const settle = async ({ maxWaitMs, verifyActive }) => {
  const start = Date.now()
  while (window[HEALTH_QUOTE_ENTRY_AB_WINDOW_KEY] === undefined && Date.now() - start < maxWaitMs) {
    // setTimeout, not requestAnimationFrame: rAF is paused in hidden tabs.
    // eslint-disable-next-line no-await-in-loop
    await new Promise(resolve => setTimeout(resolve, 50))
  }
  variation.value = readVariation({ verifyActive })
  resolved.value = true
  return variation.value
}

export function useHealthQuoteEntryAbTest () {
  const resolveVariation = ({ maxWaitMs = OPTIMIZELY_MAX_WAIT_MS, verifyActive = false } = {}) => {
    if (!import.meta.client) return Promise.resolve(null)

    // The targeted page always re-reads: an earlier result (e.g. a timed-out direct visit to a
    // variant page) must not hide a late assignment or skip the not-in-experiment check.
    if (verifyActive) {
      const previous = inFlight
      inFlight = (async () => {
        // settle() never throws, so the previous read can be awaited as-is
        await previous
        return settle({ maxWaitMs, verifyActive })
      })()
      return inFlight
    }

    // inFlight always holds the latest resolution (settled or still running), so callers
    // during a step0 re-read get the fresh result rather than an older one.
    if (!inFlight) {
      // Pre-apply for returning variant visitors; the window value wins once resolved.
      if (variation.value === null && getLocal(HEALTH_QUOTE_ENTRY_AB_STORAGE_KEY) === '1') {
        variation.value = 1
      }

      inFlight = settle({ maxWaitMs, verifyActive })
    }

    return inFlight
  }

  // Once per session, control + variant, never for unassigned users.
  const fireExposure = () => {
    if (exposureFired || variation.value === null) return
    const { $tracking } = useNuxtApp()
    if (!$tracking) return
    exposureFired = true

    $tracking.customEvent('health_quote_entry_experiment_viewed', {
      experiment_key: OPTIMIZELY_EXPERIMENTS.healthQuickQuoteVariant,
      variation_key: variation.value === 1 ? OPTIMIZELY_VARIATIONS.variant : OPTIMIZELY_VARIATIONS.control
    })
  }

  return {
    variation,
    resolved,
    isVariant: computed(() => variation.value === 1),
    resolveVariation,
    fireExposure
  }
}

// Test-only helper: clears the module-level state between specs.
export function resetHealthQuoteEntryAbTestState () {
  variation.value = null
  resolved.value = false
  inFlight = null
  exposureFired = false
}
